// not sure all this stuff will get tree-shaken, needs testing
// could reduce bundle size by only importing required modules
// eg. import { Engine, World, Body, Bodies, Constraint } from 'matter-js';
import * as Matter from 'matter-js';
import * as PolyDecomp from 'poly-decomp';
import * as PIXI from 'pixi.js';

import Layer from './layer';
import Clock from './clock';
import UIManager from './uiManager';




export default class Scene {


	constructor(args) {
		this.system = args.system;
		this.root = null; // pixi container for all layers in this scene
		this.destroyed = false;
		this.layers = {};
		this.entities = [];
		this.clocks = [];

		this.uiManager = null;
		this.uiEntities = [];

		this.paused = false;
		this.phys = false;
		this.physicsIterations = 1;
		this.pointer = false;
		this.stopPropogation = false; // set to true to block global dom/canvas click events for one frame (eg. from a PIXI graphics click event)
		
		// subclasses can override this before init
		this.pointerShape = ['0 -10 0 10','-10 0 10 0'];

		// names of graphics layers to be created in background->foreground order
		// subclasses can override this before init, but must include 'default', 'interface' and 'pointer' at some point
		this.initLayers = ['default', 'interface', 'pointer'];
	}


	/**
	 * @param {Object} data custom scene data container
	 */
	init(data) {
		this.data = data;
		this.root = new PIXI.Container();
		this.root.eventMode = 'passive';
		this.system.display.stage.addChild(this.root);
		for(let l of this.initLayers) this.addLayer(l);
		if(this.pointer) {
			this.pointer = this.system.factory.poly({
				name:'pointer',
				layer:'pointer',
				x:-100,
				y:-100,
				color: this.system.palette.white,
				shape: this.pointerShape,
				scale:1,
				interactive:false
			});
			this.pointer.sprite.g.visible = false; // invisible until pointerMove called
			this.uiEntities.push(this.pointer);
		}
		this.uiManager = new UIManager({ scene:this });
	}


	/**
	 * @param {Matter.IEngineDefinition} [args] Matter Engine options
	 * @param {Number} physicsIterations integer values over 1 will split physics update delta-per-frame into multiple incremental updates
	 */
	initPhysics(args, physicsIterations = 1) {
		this.physicsIterations = physicsIterations;
		this.phys = Matter.Engine.create(args);
		Matter.Common.setDecomp(PolyDecomp); // allow concave polygons
	}


	addLayer(name) {
		this.layers[name] = new Layer({ scene:this, system:this.system });
	}

	/**
	 * @param {Object} args
	 * @param {Function} args.task function that will be fired repeatedly, passes the number of times the function has been fired as an argument
	 * @param {Number} args.interval miliseconds between firings
	 * @param {Number} args.repeat number of times to repeat (default -1 = infinite)
	 * @param {Boolean} args.dormant begin in a dormant state
	 * @param {Boolean} args.persistent won't destroy self when number of repeats executed
	 * @param {Boolean} args.instant first tick happens immediately
	 * @param {Boolean} args.ui add clock to separate UI list
	 */
	addClock(args) {
		this.clocks.push(new Clock({
			task:args.task,
			interval:args.interval,
			repeat:args.repeat,
			dormant:args.dormant,
			persistent:args.persistent,
			instant:args.instant
		}));
	}


	update(delta, deltaNormal) {
		if(!this.paused) {
			// ———————————————————————————————————————
			// update everything including UI, because
			// uiEntities and uiClocks are subsets of
			// the entities and clocks lists...
			// ———————————————————————————————————————
			// physics
			if(this.phys) {
				let physDelta = delta / this.physicsIterations;
				let i = 0;
				while(i < this.physicsIterations) {
					Matter.Engine.update(this.phys, physDelta);
					i++;
				}
			}
			// layers
			for(let l in this.layers) {
				this.layers[l].update(delta, deltaNormal);
			}
			// clocks
			for(let c of this.clocks) {
				c.update(delta);
			}
			// entities
			for(let e of this.entities) {
				e.update(delta, deltaNormal);
			}
		} else {
			// ——————————————
			// only update UI
			// ——————————————
			// entities
			for(let e of this.uiEntities) {
				e.update(delta, deltaNormal);
			}
		}
		// UI housekeeping
		this.uiManager.update(delta, deltaNormal);
	}

	// called regularly
	cleanUp() {
		// iterate through entities backwards, remove destroyed
		for(let e = this.entities.length-1; e >= 0; e--) {
			if(this.entities[e].destroyed) {
				this.entities[e].destroyNow();
				this.entities.splice(e, 1);
			}
		}
		// ... clocks
		for(let c = this.clocks.length-1; c >= 0; c--) {
			if(this.clocks[c] == null || this.clocks[c].destroyed) {
				this.clocks[c] = null;
				this.clocks.splice(c, 1);
			}
		}
	}

	// only needs to be called when creating or removing UI elements
	cleanUpUI() {
		// iterate through UI entities backwards, remove destroyed
		for(let e = this.uiEntities.length-1; e >= 0; e--) {
			if(this.uiEntities[e].destroyed) {
				this.uiEntities[e].destroyNow();
				this.uiEntities.splice(e, 1);
			}
		}
	}


	onPointerMove(c, touchID) {
		if(this.pointer && this.system.inputManager.mousePresent) {
			this.pointer.sprite.position.x = c.x;
			this.pointer.sprite.position.y = c.y;
			this.pointer.sprite.g.visible = true;
		}
		// to be overridden ...
	}
	onPointerDown(c, touchID) {
		if(this.stopPropogation) {
			this.stopPropogation = false;
			return false; // advise sub-scene that this click should be blocked
		}
		return true;
		// c = {x,y}
		// to be overridden ...
	}
	onPointerUp(c, touchID) {
		// c = {x,y}
		// to be overridden ...
	}


	onKeyDown(key) {
		// to be overridden ...
		// console.log('Key pressed: '+key);
	}
	onKeyUp(key) {
		// to be overridden ...
		// console.log('Key released: '+key);
	}


	destroy() {
		this.destroyed = true;
		this.entities = [];
		this.pointer = null;
		for(let l in this.layers) {
			this.layers[l].destroy();
			this.layers[l] = null;
		}
		this.layers = {};
		this.system.display.stage.removeChild(this.root);
		this.root = null;
		if(this.phys) {
			Matter.World.clear(this.phys.world);
			Matter.Engine.clear(this.phys);
			window.phys = null;
			this.phys = null;
		}
	}


}